import { useTranslations } from '@aikoa/i18n/react'
import type { ReactNode } from 'react'
import { View } from 'react-native'
import Icon from '~/components/Icon'
import { Text } from '~/components/ui/text'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'
import GodRaysBackground from '../animations/GodRaysBackground'
import { UnderlinedText } from './UnderlinedText'

function BulletItem({ children, textClassName }: { children: ReactNode; textClassName?: string }) {
  return (
    <View className="flex-row items-start gap-3 py-1.5">
      <View className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" />
      <Text className={cn('flex-1 text-base leading-relaxed text-foreground', textClassName)}>
        {children}
      </Text>
    </View>
  )
}

function ProblemSection() {
  const { t } = useTranslations('home')

  return (
    <View className="w-full">
      <View className="relative left-0 mx-auto w-full max-w-6xl rounded-content bg-tertiary-foreground px-6 py-20 md:px-10 md:py-24 lg:-left-6">
        <View className="absolute left-1/2 top-0 aspect-square size-16 -translate-x-1/2 -translate-y-1/2 flex-row items-center justify-center self-center rounded-full bg-primary">
          <Icon name="account-cancel" size={30} color={designTokens.tertiaryForeground} />
        </View>
        <View className="mx-auto w-full max-w-4xl gap-8 md:gap-10">
          <View className="gap-4">
            <Text className="font-semibold text-2xl leading-tight tracking-tight text-foreground md:text-3xl">
              {t(($) => $.frontpage.realtor.problemTitle)}
            </Text>
            <Text className="text-pretty font-light leading-relaxed text-foreground">
              {t(($) => $.frontpage.realtor.valueProposition)}
            </Text>
          </View>

          <View className="mb-4 flex-col gap-4 md:flex-row">
            <View className="flex-1 gap-4 rounded-2xl bg-primary p-6 md:p-8">
              <View className="flex-row items-center gap-3">
                <View className="size-8 items-center justify-center rounded-full bg-success">
                  <Icon name="checkmark" size={15} color={designTokens.background} />
                </View>
                <Text className="font-normal text-base text-background">
                  {t(($) => $.frontpage.realtor.problemPropertiesShared)}
                </Text>
              </View>

              <Text className="font-semibold text-base leading-relaxed text-background">
                {t(($) => $.frontpage.realtor.problemPropertiesCommission)}
              </Text>
            </View>

            <View className="flex-1 gap-4 rounded-2xl border border-neutral-500/10 bg-neutral-400 p-6 sm:p-8">
              <View className="flex-row items-center gap-3">
                <View className="size-8 items-center justify-center rounded-full bg-neutral-800">
                  <Icon name="close" size={15} color="white" />
                </View>
                <Text className="font-normal text-base text-neutral-800">
                  {t(($) => $.frontpage.realtor.problemBuyersNotShared)}
                </Text>
              </View>

              <Text className="font-semibold text-base leading-relaxed text-neutral-800">
                {t(($) => $.frontpage.realtor.problemBuyersNoEarnings)}
              </Text>
            </View>
          </View>

          <Text className="font-light text-lg italic leading-relaxed text-foreground">
            {t(($) => $.frontpage.realtor.problemConclusion)}
          </Text>
        </View>
      </View>

      <View className="relative right-0 z-10 mx-auto -mt-2 w-full max-w-6xl gap-7 rounded-content bg-primary px-6 py-20 md:-right-6 md:-mt-6 md:px-10 md:py-24">
        <View className="absolute inset-x-0 -top-[75px] z-10 origin-center items-center justify-center">
          <Icon name="menu-down" size={175} color={designTokens.tertiaryForeground} />
        </View>

        <View className="mx-auto w-full max-w-4xl gap-8 md:gap-10">
          <View className="aspect-square size-16 flex-row items-center justify-center self-center rounded-full bg-tertiary-foreground">
            <Icon name="share-social" size={30} color={designTokens.primary} />
          </View>
          <View className="items-center gap-4">
            <Text className="text-center font-semibold text-2xl leading-tight tracking-tight text-background md:text-3xl">
              {t(($) => $.frontpage.realtor.solutionLock)}
            </Text>
            <Text className="text-pretty text-center font-light leading-relaxed text-background">
              {t(($) => $.frontpage.realtor.valuePitch1)}
            </Text>
          </View>

          <View className="mx-auto mb-4 w-full max-w-lg flex-1 gap-4 rounded-2xl bg-accent p-6 md:p-8">
            <View className="flex-row items-center gap-3">
              <View className="size-8 items-center justify-center rounded-full bg-background">
                <Icon name="check-all" size={15} color={designTokens.accent} />
              </View>
              <Text className="font-normal text-base text-background">
                {t(($) => $.frontpage.realtor.solutionConclusion)}
              </Text>
            </View>

            <Text className="font-semibold text-base leading-relaxed text-background">
              {t(($) => $.frontpage.realtor.solutionShare)}
            </Text>
          </View>
          <Text className="text-pretty text-center font-light text-lg italic leading-relaxed text-background">
            {t(($) => $.frontpage.realtor.valuePitch2)}
          </Text>
        </View>
      </View>
    </View>
  )
}

function HowItWorksSection() {
  const { t } = useTranslations('home')

  return (
    <View className="relative isolate mt-10 w-full transform-gpu rounded-content bg-background/30 px-6 py-16 backdrop-blur-2xl md:px-10 md:py-20">
      <GodRaysBackground
        animateOnlyWhenInView
        backgroundColor="#000"
        colors={[
          designTokens.muted,
          designTokens.accent,
          designTokens.tertiary,
          designTokens.secondary,
          designTokens.primary,
        ]}
        bloom={0.35}
        colorBloom={'#fff'}
        offsetX={0}
        offsetY={0}
        midSize={0.25}
        midIntensity={0.5}
        speed={1.5}
        intensity={1}
        density={0.3}
        className="mask-radial-from-20% mask-radial-to-70% pointer-events-none -inset-1/3 opacity-30"
      />
      <View className="size-18 absolute left-1/2 top-0 aspect-square -translate-x-1/2 -translate-y-1/2 flex-row items-center justify-center self-center rounded-full bg-background">
        <Icon name="lightbulb-on" size={30} color={designTokens.accent} />
      </View>
      <View className="mx-auto w-full max-w-4xl gap-8 md:gap-12">
        <View className="items-start gap-4">
          <Text className="font-semibold text-2xl leading-tight tracking-tight text-foreground md:text-3xl">
            {t(($) => $.frontpage.realtor.howItWorksTitle)}
          </Text>
          <Text className="text-pretty text-base leading-relaxed text-foreground">
            {t(($) => $.frontpage.realtor.howItWorksIntro)}
          </Text>
          <UnderlinedText
            animate={false}
            className="w-auto text-pretty text-lg italic leading-relaxed text-foreground">
            {t(($) => $.frontpage.realtor.poolIntro)}
          </UnderlinedText>
        </View>

        <View className="-mx-3 gap-4 md:-mx-6">
          <View className="flex-row gap-4 rounded-content-inner bg-muted/25 p-6">
            <Text className="shrink-0 font-bold text-4xl tabular-nums leading-none tracking-tight text-foreground md:w-10 md:text-6xl">
              1
            </Text>
            <Text className="flex-1 text-base leading-relaxed text-foreground">
              {t(($) => $.frontpage.realtor.step1)}
            </Text>
          </View>

          <View className="flex-row gap-4 rounded-content-inner bg-muted/25 p-6">
            <Text className="shrink-0 font-bold text-4xl tabular-nums leading-none tracking-tight text-primary md:w-10 md:text-6xl">
              2
            </Text>
            <View className="flex-1 gap-6 sm:gap-8">
              <Text className="text-base leading-relaxed text-foreground">
                {t(($) => $.frontpage.realtor.step2)}
              </Text>
            </View>
          </View>

          <View className="flex-row gap-4 rounded-content-inner bg-muted/25 p-6">
            <Text className="shrink-0 font-bold text-4xl tabular-nums leading-none tracking-tight text-accent md:w-10 md:text-6xl">
              3
            </Text>
            <Text className="flex-1 text-base leading-relaxed text-foreground">
              {t(($) => $.frontpage.realtor.step3)}
            </Text>
          </View>
          <View className="flex-row items-center gap-4 px-6 pt-4">
            <Icon name="cash-fast" size={30} color={designTokens.primary} />
            <Text className="flex-1 font-semibold text-lg text-foreground">
              {t(($) => $.frontpage.realtor.step4)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  )
}

function BenefitsSection() {
  const { t } = useTranslations('home')

  return (
    <View className="mx-auto mt-6 w-full max-w-5xl gap-2 md:gap-5">
      <View className="relative mb-8 gap-6 md:mb-5">
        <View className="mask-radial-from-25% mask-radial-to-70% pointer-events-none absolute -inset-x-1/2 inset-y-0 -bottom-[70%] z-0 backdrop-blur-sm md:-inset-x-1/4 md:-bottom-[80%]">
          <View className="h-full w-full bg-secondary/20" />
        </View>
        <View className="size-18 aspect-square flex-row items-center justify-center self-center rounded-full bg-white">
          <Icon name="sparkles" size={30} color={designTokens.accent} />
        </View>
        <Text className="drop-shadow-foreground text-center font-semibold text-3xl leading-tight tracking-tight text-background drop-shadow-sm sm:text-4xl">
          {t(($) => $.frontpage.realtor.benefitsTitle)}
        </Text>
      </View>

      <View className="gap-2 sm:gap-5 md:flex-row md:items-stretch">
        <View className="flex-1 items-center gap-6 rounded-content-inner bg-primary p-8">
          <Text className="font-semibold text-sm uppercase tracking-wider text-background">
            {t(($) => $.frontpage.realtor.benefitsYouTitle)}
          </Text>
          <View className="w-full flex-1 justify-center">
            <BulletItem textClassName="text-background">
              {t(($) => $.frontpage.realtor.benefitsYou1)}
            </BulletItem>
            <BulletItem textClassName="text-background">
              {t(($) => $.frontpage.realtor.benefitsYou2)}
            </BulletItem>
            <BulletItem textClassName="text-background">
              {t(($) => $.frontpage.realtor.benefitsYou3)}
            </BulletItem>
            <BulletItem textClassName="text-background">
              {t(($) => $.frontpage.realtor.benefitsYou4)}
            </BulletItem>
            <BulletItem textClassName="text-background">
              {t(($) => $.frontpage.realtor.benefitsYou5)}
            </BulletItem>
          </View>
        </View>

        <View className="gap-2 sm:gap-5 md:flex-1">
          <View className="flex-1 justify-evenly gap-6 rounded-content-inner bg-secondary p-8">
            <Text className="font-semibold text-sm uppercase tracking-wider text-background">
              {t(($) => $.frontpage.realtor.benefitsColleaguesTitle)}
            </Text>
            <View className="w-full">
              <BulletItem textClassName="text-background">
                {t(($) => $.frontpage.realtor.benefitsColleagues1)}
              </BulletItem>
              <BulletItem textClassName="text-background">
                {t(($) => $.frontpage.realtor.benefitsColleagues2)}
              </BulletItem>
              <BulletItem textClassName="text-background">
                {t(($) => $.frontpage.realtor.benefitsColleagues3)}
              </BulletItem>
            </View>
          </View>

          <View className="flex-1 justify-evenly gap-6 rounded-content-inner bg-tertiary-foreground p-8">
            <Text className="font-semibold text-sm uppercase tracking-wider text-foreground">
              {t(($) => $.frontpage.realtor.benefitsBuyersTitle)}
            </Text>
            <View className="w-full">
              <BulletItem>{t(($) => $.frontpage.realtor.benefitsBuyers1)}</BulletItem>
              <BulletItem>{t(($) => $.frontpage.realtor.benefitsBuyers2)}</BulletItem>
            </View>
          </View>
        </View>
      </View>
      <View className="flex-1 flex-col gap-4 rounded-content-inner bg-background p-6 md:flex-row md:gap-5 md:p-8">
        <View className="shrink-0 self-start sm:self-auto">
          <Icon name="cash-plus" size={20} color={designTokens.foreground} />
        </View>
        <Text className="flex-1 text-base italic leading-relaxed text-foreground md:text-lg">
          &ldquo;{t(($) => $.frontpage.faq.realtor.quote)}&rdquo;
        </Text>
      </View>
    </View>
  )
}

export function PublicFrontPageRealtors() {
  return (
    <View className="mx-2 gap-8 md:gap-16 lg:mx-0">
      <ProblemSection />
      <View className="mx-auto flex w-full max-w-6xl flex-col gap-8 lg:gap-24">
        <HowItWorksSection />
        <BenefitsSection />
      </View>
    </View>
  )
}
